import React from 'react';
import { useNavigate } from 'react-router-dom';
import {
  formatAlertLabel,
} from '@features/patient-coverages/components/BillingTab/eligibilityStatusConfig';
import { alpha, Box, Button, Stack, Typography, useTheme } from '@mui/material';

interface EligibilityInfoPanelProps {
  status?: string | null;
  alerts?: string[];
  patientId?: string;
  encounterId?: string;
}

function formatEligibilityStatus(status: string | undefined | null): { label: string; color: 'success' | 'error' | 'warning' } {
  if (!status) return { label: 'Unknown', color: 'error' };
  if (status === 'eligible') return { label: 'Active', color: 'success' };
  if (status === 'needs_review') return { label: 'Needs Review', color: 'warning' };
  if (status === 'blocked') return { label: 'Blocked', color: 'error' };
  return { label: 'Unknown', color: 'error' };
}

export const EligibilityInfoPanel: React.FC<EligibilityInfoPanelProps> = ({
  status: statusProp,
  alerts = [],
  patientId,
  encounterId,
}) => {
  const navigate = useNavigate();
  const theme = useTheme();
  const status = formatEligibilityStatus(statusProp);
  const statusColor = theme.palette[status.color].main;

  return (
    <Box
      sx={{
        border: '1px solid',
        borderColor: alpha(statusColor, 0.4),
        borderRadius: 1,
        overflow: 'hidden',
      }}
    >
      {/* Coverage Status */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, px: 2, py: 1.5 }}>
        <Box
          sx={{
            width: 10,
            height: 10,
            borderRadius: '50%',
            bgcolor: statusColor,
            flexShrink: 0,
          }}
        />
        <Typography variant="subtitle2" fontWeight={600}>
          Coverage: {status.label}
        </Typography>
      </Box>

      {/* Action Items */}
      {alerts.length > 0 && (
        <Box sx={{ px: 2, pb: 1.5 }}>
          <Typography variant="caption" color="text.secondary" sx={{ mb: 0.5, display: 'block' }}>
            Needs attention:
          </Typography>
          <Stack spacing={0.25}>
            {alerts.map((alert) => (
              <Box key={alert} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Box
                  sx={{
                    width: 4,
                    height: 4,
                    borderRadius: '50%',
                    bgcolor: 'text.disabled',
                    flexShrink: 0,
                  }}
                />
                <Typography variant="body2" color="text.secondary">
                  {formatAlertLabel(alert)}
                </Typography>
              </Box>
            ))}
          </Stack>
        </Box>
      )}

      {/* Review link */}
      {patientId && (
        <Box sx={{ px: 2, pb: 1.5 }}>
          <Button
            size="small"
            variant="text"
            onClick={() =>
              navigate(`/patient/${patientId}`, {
                state: { tab: 'billing', encounterId },
              })
            }
            sx={{ textTransform: 'none', fontSize: '0.8125rem', p: 0, minWidth: 'auto' }}
          >
            View full details
          </Button>
        </Box>
      )}
    </Box>
  );
};
